import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAppAuth } from '../hooks/useAppAuth';
import { useRole } from '../hooks/useRole';
import { createApiClient, invoicesService } from '../services/api';
import { InvoiceStatus } from '../types';
import type { InvoiceDto } from '../types';

const STATUS_LABELS: Record<InvoiceStatus, string> = {
  [InvoiceStatus.Draft]: 'Draft',
  [InvoiceStatus.Approved]: 'Approved',
  [InvoiceStatus.Sent]: 'Sent',
  [InvoiceStatus.Paid]: 'Paid',
  [InvoiceStatus.Overdue]: 'Overdue',
  [InvoiceStatus.Cancelled]: 'Cancelled',
};
const STATUS_COLOURS: Record<InvoiceStatus, { bg: string; text: string }> = {
  [InvoiceStatus.Draft]: { bg: '#f3f4f6', text: '#374151' },
  [InvoiceStatus.Approved]: { bg: '#dbeafe', text: '#1e40af' },
  [InvoiceStatus.Sent]: { bg: '#e0e7ff', text: '#3730a3' },
  [InvoiceStatus.Paid]: { bg: '#dcfce7', text: '#15803d' },
  [InvoiceStatus.Overdue]: { bg: '#fee2e2', text: '#991b1b' },
  [InvoiceStatus.Cancelled]: { bg: '#fef3c7', text: '#92400e' },
};

export default function InvoicesPage() {
  const { getToken, account } = useAppAuth();
  const { role } = useRole();
  const isCustomer = role === 'customer';

  const [invoices, setInvoices] = useState<InvoiceDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | ''>('');
  const [downloading, setDownloading] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [emailing, setEmailing] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!account) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const api = createApiClient(getToken);
      const svc = invoicesService(api);
      const data = isCustomer ? await svc.getForMe() : await svc.getMine();
      setInvoices(data);
    } catch (e: any) {
      setError(e.message ?? 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [account, getToken, isCustomer]);

  useEffect(() => { load(); }, [load]);

  const handleDownload = async (id: string) => {
    setDownloading(id);
    try {
      const api = createApiClient(getToken);
      const blobUrl = await invoicesService(api).downloadPdf(id);
      window.open(blobUrl, '_blank');
    } catch (e: any) {
      alert(e.message ?? 'Failed to download invoice PDF.');
    } finally {
      setDownloading(null);
    }
  };

  // Customers are notified in-app when a provider issues an invoice; nothing is emailed unless
  // they ask for it here, which always sends to their own registered address.
  const handleEmailToMe = async (id: string) => {
    setEmailing(id);
    try {
      const api = createApiClient(getToken);
      const { sentTo } = await invoicesService(api).emailToMe(id);
      alert(`Invoice emailed to ${sentTo}.`);
    } catch (e: any) {
      alert(e.response?.data?.detail ?? e.message ?? 'Failed to email this invoice.');
    } finally {
      setEmailing(null);
    }
  };

  const handleCancel = async (id: string) => {
    if (!confirm('Cancel this draft invoice?')) return;
    setCancelling(id);
    try {
      const api = createApiClient(getToken);
      await invoicesService(api).cancel(id);
      await load();
    } catch (e: any) {
      alert(e.message ?? 'Failed to cancel invoice.');
    } finally {
      setCancelling(null);
    }
  };

  const visible = statusFilter === '' ? invoices : invoices.filter(i => i.status === statusFilter);
  const unpaidTotal = invoices
    .filter(i => i.status === InvoiceStatus.Sent || i.status === InvoiceStatus.Overdue)
    .reduce((sum, i) => sum + i.total, 0);

  return (
    <div style={{ width: '100%', padding: '24px 16px' }}>
      <div style={{ marginBottom: 20 }}>
        <Link to="/my-dashboard" style={{ fontSize: 13, color: '#6b7280', textDecoration: 'none' }}>← Dashboard</Link>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '8px 0 4px' }}>
              {isCustomer ? 'My Invoices' : 'Invoices'}
            </h1>
            <p style={{ fontSize: 14, color: '#6b7280', margin: 0 }}>
              {isCustomer ? 'Invoices you have received from providers.' : 'Invoices you have issued to customers.'}
            </p>
          </div>
          {!isCustomer && (
            <div style={{ display: 'flex', gap: 8 }}>
              <Link to="/invoices/create" style={{
                fontSize: 13, fontWeight: 600, padding: '8px 14px', borderRadius: 8,
                background: '#2563eb', color: '#fff', textDecoration: 'none',
              }}>
                Generate Invoice
              </Link>
              <Link to="/invoices/settings" style={{
                fontSize: 13, fontWeight: 600, padding: '8px 14px', borderRadius: 8,
                border: '1px solid #d1d5db', color: '#374151', textDecoration: 'none',
              }}>
                Settings
              </Link>
            </div>
          )}
        </div>
      </div>

      {isCustomer && unpaidTotal > 0 && (
        <div style={{
          background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10,
          padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#92400e',
        }}>
          Outstanding balance: <strong>£{unpaidTotal.toFixed(2)}</strong>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {(['', InvoiceStatus.Draft, InvoiceStatus.Sent, InvoiceStatus.Paid, InvoiceStatus.Overdue] as const).map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            style={{
              fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 20,
              border: '1px solid ' + (statusFilter === s ? '#2563eb' : '#d1d5db'),
              background: statusFilter === s ? '#eff6ff' : '#fff',
              color: statusFilter === s ? '#1d4ed8' : '#374151',
              cursor: 'pointer',
            }}
          >
            {s === '' ? 'All' : STATUS_LABELS[s]}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af' }}>Loading…</div>
      ) : error ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#dc2626' }}>⚠️ {error}</div>
      ) : visible.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <p style={{ fontSize: 32, margin: '0 0 8px' }}>🧾</p>
          <p style={{ color: '#9ca3af', fontSize: 14 }}>
            {isCustomer ? 'No invoices yet.' : 'No invoices yet. Generate an invoice from your completed jobs.'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {visible.map(inv => {
            const colours = STATUS_COLOURS[inv.status];
            return (
              <div key={inv.id} style={{
                display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
                padding: '14px 16px', borderRadius: 10, border: '1px solid #e5e7eb',
                background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <Link to={`/invoices/${inv.id}`} style={{ fontSize: 14, fontWeight: 600, color: '#111827', textDecoration: 'none' }}>
                    {inv.invoiceNumber}
                  </Link>
                  <p style={{ fontSize: 12, color: '#6b7280', margin: '2px 0 0' }}>
                    {isCustomer ? inv.providerName : (inv.customerBusinessName ?? inv.customerName)}
                    {' · '}
                    {inv.jobReferences.join(', ')}
                  </p>
                  <p style={{ fontSize: 11, color: '#9ca3af', margin: '2px 0 0' }}>
                    Issued {new Date(inv.issuedAt).toLocaleDateString('en-GB')}
                    {inv.dueDate && ` · Due ${new Date(inv.dueDate).toLocaleDateString('en-GB')}`}
                  </p>
                </div>

                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <p style={{ fontSize: 14, fontWeight: 700, color: '#111827', margin: 0 }}>£{inv.total.toFixed(2)}</p>
                  {inv.isVatRegistered && (
                    <p style={{ fontSize: 11, color: '#9ca3af', margin: '2px 0 0' }}>inc. £{inv.vatTotal.toFixed(2)} VAT</p>
                  )}
                </div>

                <span style={{
                  fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 12,
                  background: colours.bg, color: colours.text, flexShrink: 0,
                }}>
                  {STATUS_LABELS[inv.status]}
                </span>

                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button
                    onClick={() => handleDownload(inv.id)}
                    disabled={downloading === inv.id}
                    style={{
                      fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 8,
                      border: '1px solid #2563eb', cursor: 'pointer',
                      background: downloading === inv.id ? '#e5e7eb' : '#fff', color: '#2563eb',
                    }}
                  >
                    {downloading === inv.id ? 'Opening…' : 'Download'}
                  </button>
                  {isCustomer && inv.status !== InvoiceStatus.Draft && inv.status !== InvoiceStatus.Cancelled && (
                    <button
                      data-testid={`invoice-email-to-me-${inv.id}`}
                      onClick={() => handleEmailToMe(inv.id)}
                      disabled={emailing === inv.id}
                      title="Send a copy of this invoice to your registered email address"
                      style={{
                        fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 8,
                        border: '1px solid #2563eb', cursor: 'pointer',
                        background: emailing === inv.id ? '#e5e7eb' : '#fff', color: '#2563eb',
                      }}
                    >
                      {emailing === inv.id ? 'Sending…' : 'Email to me'}
                    </button>
                  )}
                  {!isCustomer && inv.status === InvoiceStatus.Draft && (
                    <button
                      onClick={() => handleCancel(inv.id)}
                      disabled={cancelling === inv.id}
                      style={{
                        fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 8,
                        border: '1px solid #dc2626', cursor: 'pointer', background: '#fff', color: '#dc2626',
                      }}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
